import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube, MagnifyingGlass, Scroll, Wrench } from "@phosphor-icons/react";

const STATUS_META = {
  in: { label: "In Stock", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  low: { label: "Low Stock", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  out: { label: "Out of Stock", cls: "bg-red-50 text-red-700 border-red-200" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status];
  return (
    <span
      data-testid={`stock-status-${status}`}
      className={`text-[10px] font-mono tracking-[0.12em] uppercase px-2 py-0.5 rounded-sm border inline-block ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

export default function StockOnHand({ embedded = false }) {
  const { isViewer, canSeePricing, maskMoney } = useAuth();
  const [rolls, setRolls] = useState([]);
  const [products, setProducts] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [series, setSeries] = useState([]);
  const [search, setSearch] = useState("");
  const [filterMfr, setFilterMfr] = useState("all");
  const [filterType, setFilterType] = useState("all"); // all | rolls | count

  useEffect(() => {
    Promise.all([
      api.get("/products"), api.get("/manufacturers"),
      api.get("/series"), api.get("/rolls"),
    ]).then(([p, m, s, r]) => {
      setProducts(p.data); setMfrs(m.data); setSeries(s.data); setRolls(r.data);
    });
  }, []);

  const mfrMap = Object.fromEntries(mfrs.map((m) => [m.id, m]));
  const seriesMap = Object.fromEntries(series.map((s) => [s.id, s]));

  const tableRows = useMemo(() => {
    const byProduct = {};
    for (const r of rolls) {
      if (!byProduct[r.product_id]) byProduct[r.product_id] = { meters: 0, count: 0 };
      byProduct[r.product_id].meters += r.current_length_m;
      byProduct[r.product_id].count += 1;
    }
    return products
      .filter((p) => filterMfr === "all" || p.manufacturer_id === filterMfr)
      .filter((p) => filterType === "all" || p.inventory_type === filterType)
      .filter((p) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return p.name.toLowerCase().includes(q) || p.base_sku.toLowerCase().includes(q);
      })
      .map((p) => {
        const isCount = p.inventory_type === "count";
        const stock = isCount ? p.current_count : (byProduct[p.id]?.meters || 0);
        const threshold = isCount ? p.low_stock_threshold_count : p.low_stock_threshold_meters;
        const rollCount = byProduct[p.id]?.count || 0;
        const price = isCount ? p.price_per_unit : p.price_per_meter;
        const value = canSeePricing && price != null ? stock * price : null;
        const isLow = stock <= threshold;
        const status = stock <= 0 ? "out" : (stock <= threshold ? "low" : "in");
        return { p, isCount, stock, threshold, rollCount, value, isLow, status };
      })
      .sort((a, b) => Number(b.isLow) - Number(a.isLow) || b.stock - a.stock);
  }, [products, rolls, filterMfr, filterType, search, canSeePricing]);

  const totals = useMemo(() => {
    let totalMeters = 0, totalUnits = 0, totalValue = 0;
    let inCount = 0, lowCount = 0, outCount = 0;
    for (const r of tableRows) {
      if (r.isCount) totalUnits += r.stock;
      else totalMeters += r.stock;
      if (r.value != null) totalValue += r.value;
      if (r.status === "in") inCount += 1;
      else if (r.status === "low") lowCount += 1;
      else outCount += 1;
    }
    return { totalMeters, totalUnits, totalValue, rowCount: tableRows.length, inCount, lowCount, outCount };
  }, [tableRows]);

  return (
    <div className={embedded ? "" : "p-8 max-w-[1500px]"} data-testid="stock-on-hand-page">
      {embedded ? (
        <div className="flex items-center gap-3 mb-6">
          <Cube size={22} className="text-slate-400" />
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Stock on Hand</h2>
        </div>
      ) : (
        <header className="mb-8">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Inventory · Live View</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Cube size={32} /> Stock on Hand
          </h1>
          <p className="text-sm text-slate-600 mt-2">Aggregated inventory across all rolls and count items.</p>
        </header>
      )}

      {isViewer ? (
        <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white mb-6" data-testid="viewer-status-summary">
          <div className="p-5 border-r border-slate-200">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">In Stock</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums text-emerald-600">{totals.inCount}</div>
          </div>
          <div className="p-5 border-r border-slate-200">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Low Stock</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums text-amber-600">{totals.lowCount}</div>
          </div>
          <div className="p-5 border-r border-slate-200">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Out of Stock</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums text-red-600">{totals.outCount}</div>
          </div>
          <div className="p-5">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Products in view</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums">{totals.rowCount}</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white mb-6">
          <div className="p-5 border-r border-slate-200">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Total Meters</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums">{totals.totalMeters.toFixed(2)}</div>
          </div>
          <div className="p-5 border-r border-slate-200">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Total Units</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums">{totals.totalUnits}</div>
          </div>
          <div className="p-5 border-r border-slate-200">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Products in view</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums">{totals.rowCount}</div>
          </div>
          <div className="p-5">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Stock Value (Sell)</div>
            <div className="font-mono text-2xl font-semibold mt-1 tabular-nums text-blue-600">
              {canSeePricing ? maskMoney(`$${totals.totalValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`) : "—"}
            </div>
            {!canSeePricing && <div className="text-[10px] text-slate-400 mt-0.5">admin only</div>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by product or SKU…" className="pl-9 h-10 rounded-md bg-white" data-testid="soh-search" />
        </div>
        <Select value={filterMfr} onValueChange={setFilterMfr}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Manufacturer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All manufacturers</SelectItem>
            {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="rolls">Rolls</SelectItem>
            <SelectItem value="count">Count</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {tableRows.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Cube size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">No products match.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Manufacturer · Series</th>
                <th className="px-4 py-3">Base SKU</th>
                {isViewer ? (
                  <th className="px-4 py-3 text-right">Availability</th>
                ) : (
                  <>
                    <th className="px-4 py-3 text-right">Stock</th>
                    <th className="px-4 py-3 text-right">Threshold</th>
                    <th className="px-4 py-3 text-right">Rolls</th>
                    {canSeePricing && <th className="px-4 py-3 text-right">Value</th>}
                    <th className="px-4 py-3"></th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {tableRows.map(({ p, isCount, stock, threshold, rollCount, value, isLow, status }) => (
                <tr key={p.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${isLow && !isViewer ? "bg-red-50/40" : ""}`}>
                  <td className="px-4 py-3">
                    <span className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 ${
                      isCount ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}>
                      {isCount ? <Wrench size={10} /> : <Scroll size={10} />}
                      {isCount ? "Count" : "Rolls"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {!isCount && <div className="w-6 h-6 border border-slate-200 rounded-sm" style={{ background: p.color_hex || "#e2e8f0" }} />}
                      {isViewer ? (
                        <span className="font-medium">{p.name}</span>
                      ) : (
                        <Link to={isCount ? "/products" : `/browse/product/${p.id}`} className="font-medium hover:text-blue-600">
                          {p.name}
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{mfrMap[p.manufacturer_id]?.name} · {seriesMap[p.series_id]?.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.base_sku}</td>
                  {isViewer ? (
                    <td className="px-4 py-3 text-right"><StatusBadge status={status} /></td>
                  ) : (
                    <>
                      <td className={`px-4 py-3 font-mono text-right tabular-nums font-semibold ${isLow ? "text-red-600" : ""}`}>
                        {isCount ? `${stock} units` : `${stock.toFixed(2)}m`}
                      </td>
                      <td className="px-4 py-3 font-mono text-right tabular-nums text-slate-500">
                        {isCount ? `${threshold} units` : `${threshold}m`}
                      </td>
                      <td className="px-4 py-3 font-mono text-right tabular-nums">{isCount ? "—" : rollCount}</td>
                      {canSeePricing && (
                        <td className="px-4 py-3 font-mono text-right tabular-nums text-blue-600">
                          {value != null ? maskMoney(`$${value.toFixed(2)}`) : "—"}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        {isLow && <span className="text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm">LOW</span>}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
